import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { api, API } from "../lib/api";
import { parseFiles, stripFileBlocks } from "../lib/parseFiles";
import ChatPanel from "../components/workspace/ChatPanel";
import EditorPanel from "../components/workspace/EditorPanel";
import PreviewPanel from "../components/workspace/PreviewPanel";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import CreditBadge from "../components/CreditBadge";
import CreditAlert from "../components/CreditAlert";
import DeployModal from "../components/DeployModal";
import { Lightning, ArrowLeft, CircleNotch, FloppyDisk, Check, Rocket, Code, Eye } from "@phosphor-icons/react";

export default function Workspace() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshUser } = useAuth();

  const [project, setProject] = useState(null);
  const [files, setFiles] = useState([]);
  const [activeFile, setActiveFile] = useState(null);
  const [messages, setMessages] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const [saveState, setSaveState] = useState("saved"); // saved | saving | dirty
  const [mobileTab, setMobileTab] = useState("chat");
  const [showDeploy, setShowDeploy] = useState(false);
  const [showCode, setShowCode] = useState(false);

  const initialFired = useRef(false);
  const saveTimer = useRef(null);

  useEffect(() => {
    api
      .get(`/api/projects/${projectId}`)
      .then((res) => {
        setProject(res.data);
        setFiles(res.data.files || []);
        setMessages(res.data.messages || []);
        if (res.data.files?.length) setActiveFile(res.data.files[0].name);
        const initialPrompt = location.state?.initialPrompt;
        if (initialPrompt && !res.data.messages?.length && !initialFired.current) {
          initialFired.current = true;
          setTimeout(() => sendPrompt(initialPrompt), 300);
        }
      })
      .catch(() => {
        toast.error("Project not found");
        navigate("/");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const sendPrompt = useCallback(
    async (prompt) => {
      setMessages((m) => [...m, { role: "user", content: prompt }]);
      setStreaming(true);
      setStreamingText("");
      const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
      try {
        let start;
        try {
          start = await api.post("/api/ai/generate", { project_id: projectId, prompt });
        } catch (e) {
          if (e.response?.status === 402) {
            toast.error(e.response.data?.detail || "Out of credits");
            setMessages((m) => m.slice(0, -1));
            setStreaming(false);
            navigate("/billing");
            return;
          }
          throw e;
        }
        const jobId = start.data.job_id;
        let done = false;
        let polls = 0;
        const MAX_POLLS = 220; // ~5.5 min safety cap
        while (!done) {
          await sleep(1500);
          polls += 1;
          if (polls > MAX_POLLS) throw new Error("Generation timed out");
          const res = await api.get(`/api/ai/generation/${jobId}`);
          const { status, content, files, error, credits_remaining } = res.data;
          if (content) {
            const parsed = parseFiles(content);
            if (parsed.length) {
              setFiles(parsed);
              setActiveFile((prev) => {
                const streamingFile = parsed.find((f) => f.streaming);
                return streamingFile ? streamingFile.name : prev || parsed[0].name;
              });
            }
            setStreamingText(stripFileBlocks(content));
          }
          if (status === "done") {
            done = true;
            const finalFiles = files && files.length ? files : parseFiles(content);
            if (finalFiles.length) {
              setFiles(finalFiles);
              setActiveFile((prev) =>
                finalFiles.find((f) => f.name === prev) ? prev : finalFiles[0].name
              );
            }
            setMessages((m) => [...m, { role: "assistant", content }]);
            if (credits_remaining !== null && credits_remaining !== undefined) {
              refreshUser();
              if (credits_remaining <= 0) {
                toast.error("You've run out of credits. Buy a top-up to keep building.");
              } else if (credits_remaining < 20) {
                toast.warning(`Low on credits: ${credits_remaining} left.`);
              }
            }
          } else if (status === "error") {
            done = true;
            throw new Error(error || "Generation failed");
          }
        }
      } catch (e) {
        toast.error(e.message || "Generation failed");
        setMessages((m) => [...m, { role: "assistant", content: "⚠️ Generation failed. Please try again." }]);
      } finally {
        setStreaming(false);
        setStreamingText("");
      }
    },
    [projectId, navigate, refreshUser]
  );

  const handleFileChange = (name, content) => {
    setFiles((prev) => prev.map((f) => (f.name === name ? { ...f, content } : f)));
    setSaveState("dirty");
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => saveFiles(name, content), 1200);
  };

  const saveFiles = async (name, content) => {
    setSaveState("saving");
    try {
      const updated = files.map((f) => (f.name === name ? { ...f, content } : f));
      await api.put(`/api/projects/${projectId}`, {
        files: updated.map(({ streaming, ...rest }) => rest),
      });
      setSaveState("saved");
    } catch {
      setSaveState("dirty");
    }
  };

  if (!project) {
    return (
      <div className="h-screen flex items-center justify-center bg-forge-bg">
        <CircleNotch size={26} className="text-forge-primary animate-spin" />
      </div>
    );
  }

  const cleanFiles = files.map(({ streaming, ...f }) => f);

  return (
    <div className="h-screen flex flex-col bg-forge-bg overflow-hidden">
      {/* Header */}
      <header className="h-12 shrink-0 flex items-center justify-between px-4 border-b border-forge-border bg-forge-bg">
        <div className="flex items-center gap-3 min-w-0">
          <button
            data-testid="back-to-dashboard"
            onClick={() => navigate("/")}
            className="text-neutral-400 hover:text-white transition-colors"
          >
            <ArrowLeft size={18} />
          </button>
          <Lightning size={18} weight="fill" className="text-forge-primary shrink-0" />
          <span className="text-sm font-medium text-white truncate max-w-[40vw]" data-testid="project-name">
            {project.name}
          </span>
        </div>
        <div className="flex items-center gap-3 text-xs text-neutral-400 font-mono">
          <CreditBadge compact />
          <button
            data-testid="toggle-code-btn"
            onClick={() => { setShowCode((v) => !v); if (mobileTab === "code") setMobileTab("preview"); }}
            className={`flex items-center gap-1.5 border px-3 py-1.5 rounded-sm transition-colors ${showCode ? "border-forge-primary text-forge-primary bg-forge-primary/10" : "border-forge-border text-neutral-300 hover:bg-forge-hover"}`}
            title={showCode ? "Hide code" : "Show code"}
          >
            <Code size={14} /> {showCode ? "Hide code" : "Code"}
          </button>
          <button
            data-testid="open-deploy-btn"
            onClick={() => setShowDeploy(true)}
            className="flex items-center gap-1.5 bg-forge-primary text-black font-medium px-3 py-1.5 rounded-sm hover:bg-yellow-400 transition-colors"
          >
            <Rocket size={14} weight="fill" /> Deploy
          </button>
          {saveState === "saving" && <span className="flex items-center gap-1"><CircleNotch size={13} className="animate-spin" /> saving</span>}
          {saveState === "saved" && <span className="flex items-center gap-1 text-green-500"><Check size={13} /> saved</span>}
          {saveState === "dirty" && <span className="flex items-center gap-1 text-forge-primary"><FloppyDisk size={13} /> unsaved</span>}
        </div>
      </header>

      <CreditAlert />

      {/* Mobile tab switcher */}
      <div className="lg:hidden flex border-b border-forge-border shrink-0">
        {(showCode ? ["chat", "code", "preview"] : ["chat", "preview"]).map((t) => (
          <button
            key={t}
            onClick={() => setMobileTab(t)}
            className={`flex-1 py-2 text-xs uppercase tracking-[0.15em] transition-colors ${
              mobileTab === t ? "text-forge-primary border-b-2 border-forge-primary" : "text-neutral-500"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Workspace grid */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 overflow-hidden">
        <div className={`${mobileTab === "chat" ? "block" : "hidden"} lg:block ${showCode ? "lg:col-span-3" : "lg:col-span-4"} border-r border-forge-border h-full overflow-hidden`}>
          <ChatPanel messages={messages} streaming={streaming} streamingText={streamingText} onSend={sendPrompt} />
        </div>
        {showCode && (
          <div className={`${mobileTab === "code" ? "block" : "hidden"} lg:block lg:col-span-5 border-r border-forge-border h-full overflow-hidden`}>
            <EditorPanel
              files={files}
              activeFile={activeFile}
              setActiveFile={setActiveFile}
              onChange={handleFileChange}
              streaming={streaming}
            />
          </div>
        )}
        <div className={`${mobileTab === "preview" ? "block" : "hidden"} lg:block ${showCode ? "lg:col-span-4" : "lg:col-span-8"} h-full overflow-hidden`}>
          <PreviewPanel files={cleanFiles} />
        </div>
      </div>

      <DeployModal
        projectId={projectId}
        open={showDeploy}
        onOpenChange={setShowDeploy}
        onDeployed={() => {}}
      />
    </div>
  );
}
